/**
 * 満足度サイクル入力コンポーネント
 *
 * クリックするたびに 😞→😕→😐→🙂→😊→(未設定)→😞... とサイクルする。
 * 省スペースで週間ビューなどに適している。
 */

import { useState, useCallback, useEffect } from 'react'
import { cn } from '@/lib/utils'
import { daily_satisfactions_path } from '@/lib/routes'

const EMOJIS = [
  '',
  '\u{1F61E}',
  '\u{1F615}',
  '\u{1F610}',
  '\u{1F642}',
  '\u{1F60A}',
] as const
const LABELS = [
  '未設定',
  'とても不満',
  'やや不満',
  'ふつう',
  'やや満足',
  'とても満足',
] as const

interface SatisfactionCyclerProps {
  level: number | null
  date: string
  size?: 'xs' | 'sm' | 'md'
  className?: string
}

const SIZE_CLASSES = {
  xs: 'text-xs h-5 w-5',
  sm: 'text-sm h-7 w-7',
  md: 'text-base h-8 w-8',
} as const

export function SatisfactionCycler({
  level,
  date,
  size = 'sm',
  className,
}: SatisfactionCyclerProps) {
  const [current, setCurrent] = useState(level)
  const [pending, setPending] = useState(false)

  useEffect(() => {
    setCurrent(level)
  }, [level])

  const handleClick = useCallback(async () => {
    if (pending) return

    // サイクル: null→1→2→3→4→5→null→1...
    const nextLevel = current === null ? 1 : current >= 5 ? null : current + 1

    const previousLevel = current
    setCurrent(nextLevel)

    // null（未設定）に戻す場合はレベル0で送信しない → level 1 に戻す代わりにスキップ
    // 実際にはnullは「記録なし」なので、level 1-5のみ保存
    if (nextLevel === null) {
      // 未設定に戻す場合は保存しない（表示のみリセット）
      return
    }

    setPending(true)
    try {
      const csrfToken =
        document.querySelector<HTMLMetaElement>('meta[name="csrf-token"]')
          ?.content || ''
      const response = await fetch(daily_satisfactions_path(), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': csrfToken,
        },
        body: JSON.stringify({ date, level: nextLevel }),
      })

      if (!response.ok) {
        setCurrent(previousLevel)
      }
    } catch {
      setCurrent(previousLevel)
    } finally {
      setPending(false)
    }
  }, [pending, current, date])

  const emoji =
    current !== null && current >= 1 && current <= 5 ? EMOJIS[current] : null
  const label =
    current !== null && current >= 1 && current <= 5
      ? LABELS[current]
      : LABELS[0]

  return (
    <button
      onClick={handleClick}
      disabled={pending}
      className={cn(
        'flex items-center justify-center rounded-full transition-all',
        'hover:bg-muted/80 active:scale-90',
        SIZE_CLASSES[size],
        pending && 'animate-pulse opacity-50',
        className
      )}
      aria-label={`気分: ${label}（クリックで変更）`}
      title={`${label}（クリックで変更）`}
    >
      {emoji ?? <span className="text-muted-foreground/40">·</span>}
    </button>
  )
}
